/**
 * Pure validation for the composer's photo action (R2), split out from
 * `useComposerPhotoUpload.ts` so the count/type/size branching is
 * unit-testable without a network stub. Reuses `useChatPhotoUpload.ts`'s own
 * type and size ceiling rather than a second copy of the same numbers.
 */
import {
  CHAT_PHOTO_CONTENT_TYPES,
  MAX_CHAT_PHOTO_BYTES,
} from "./useChatPhotoUpload";

export type typeRejectedPhoto = {
  file: File;
  reason: "tooMany" | "wrongType" | "tooLarge";
};

export type typePhotoSelectionResult = {
  accepted: File[];
  rejected: typeRejectedPhoto[];
};

/**
 * Up to 10 photos per message (Q205). `currentCount` is how many the
 * composer already holds (staged or sent-pending); `incomingFiles` is a
 * fresh file-picker selection, evaluated in the order the fan picked them so
 * the first ones through the door fill the remaining slots.
 */
export function validatePhotoSelection(input: {
  currentCount: number;
  incomingFiles: readonly File[];
  maxPhotos: number;
}): typePhotoSelectionResult {
  const accepted: File[] = [];
  const rejected: typeRejectedPhoto[] = [];
  let count = input.currentCount;

  for (const file of input.incomingFiles) {
    if (count >= input.maxPhotos) {
      rejected.push({ file, reason: "tooMany" });
      continue;
    }
    if (!(CHAT_PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      rejected.push({ file, reason: "wrongType" });
      continue;
    }
    if (file.size < 1 || file.size > MAX_CHAT_PHOTO_BYTES) {
      rejected.push({ file, reason: "tooLarge" });
      continue;
    }
    accepted.push(file);
    count += 1;
  }

  return { accepted, rejected };
}
